import type { Summary } from "../api";
import { compact, duration, full, pct } from "./format";

/**
 * The window's headline numbers.
 *
 * Stat tiles rather than a chart: each of these is a single value, and a
 * single value has no shape to plot. The compact form is what the eye
 * reads; the full number is in the title attribute for anybody who needs
 * it exactly.
 */
export default function Kpis({ summary }: { summary: Summary }) {
  const tiles: { label: string; value: string; exact?: string; hint: string }[] = [
    {
      label: "Leads",
      value: compact(summary.leads_created),
      exact: full(summary.leads_created),
      hint: "Journey groups created in the window — one case, however many follow-up journeys it holds",
    },
    {
      label: "Calls",
      value: compact(summary.calls_total),
      exact: full(summary.calls_total),
      hint: "Every call row, including ones queued or cancelled before dialling",
    },
    {
      label: "Attempts",
      value: compact(summary.attempts),
      exact: full(summary.attempts),
      hint: "Calls that actually left our side",
    },
    {
      label: "Connected",
      value: compact(summary.connected),
      exact: full(summary.connected),
      hint: "Attempts that reached a human — voicemail excluded",
    },
    {
      label: "Connect rate",
      value: pct(summary.connect_rate),
      hint: "Connected ÷ attempts. Rows never dialled are not counted against it",
    },
    {
      label: "Attempts / lead",
      value: summary.attempts_per_lead?.toFixed(2) ?? "—",
      hint: "The dialling ladder: how many times a lead was tried",
    },
    {
      label: "Avg talk",
      value: duration(summary.avg_talk_seconds),
      hint: "Mean over connected calls only — averaging across no-answers would read near zero",
    },
    {
      label: "Trigger failures",
      value: compact(summary.trigger_failed),
      exact: full(summary.trigger_failed),
      hint: "Calls that never got out: no agent config, no number, provider error. A platform fault, not customer behaviour",
    },
  ];

  return (
    <div className="kpis">
      {tiles.map((t) => (
        <div className="kpi" key={t.label} title={t.hint}>
          <div className="kpi-label">{t.label}</div>
          <div className="kpi-value" title={t.exact}>
            {t.value}
          </div>
        </div>
      ))}
    </div>
  );
}
